import { ReactNode, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from "react-redux";
import { store } from "store";

type BaseModalProps = {
    title?: ReactNode;
    content: ReactNode;
    onClose?: () => void;
    actions?: ReactNode;
    footer?: ReactNode | (() => ReactNode);
    closeable?: boolean;
    fullWidth?: boolean;
    fullHeight?: boolean;
};

const BaseModal = ({
    title,
    content,
    onClose,
    actions,
    footer,
    closeable = true,
    fullWidth = false,
    fullHeight = false
}: BaseModalProps) => {
    const [visible, setVisible] = useState(true);

    const close = () => {
        setVisible(false);
        onClose?.();
    };

    useEffect(() => {
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && closeable) {
                e.stopPropagation();
                e.preventDefault();
                close()
            };
        };
        document.addEventListener('keydown', handleEsc);
        return () => document.removeEventListener('keydown', handleEsc);
    }, [closeable]);

    if (!visible) return null;

    return (
        <Provider store={store}>
            <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[2147483648]">
                <div
                    className={`
                        bg-white rounded-lg min-w-[300px] p-5
                        max-w-[calc(100%-40px)] max-h-[calc(100%-40px)]
                        shadow-[0_4px_20px_rgba(0,0,0,0.2)]
                        animate-fadeIn flex flex-col
                        ${fullWidth ? 'w-[calc(100%-40px)]' : ''}
                        ${fullHeight ? 'h-[calc(100%-40px)]' : ''}
                    `}
                >
                    <div className="flex items-center justify-between mb-2">
                        {title && <div className="text-lg font-semibold">{title}</div>}
                        {closeable && (
                            <button
                                className="text-gray-500 hover:text-gray-800"
                                onClick={close}
                            >
                                ✕
                            </button>
                        )}
                    </div>
                    <div className="text-sm overflow-y-auto flex-1">{content}</div>
                    {footer && <div className="mt-2">{typeof footer === "function" ? footer() : footer}</div>}
                    {actions && <div className="flex justify-end gap-2 mt-2">{actions}</div>}
                </div>
            </div>
        </Provider>
    );
};

type ConfirmProps = {
    title: ReactNode;
    content: ReactNode;
    onOk?: () => void;
    onCancel?: () => void;
    okText?: string;
    cancelText?: string;
};

type ModalProps = {
    title?: ReactNode;
    content: ReactNode;
    onClose?: () => void;
    fullWidth?: boolean;
    fullHeight?: boolean;
    closeable?: boolean;
    footer?: ReactNode | (() => ReactNode);
};

export const AppAlert = {
    confirm(props: ConfirmProps) {
        const container = document.createElement('div');
        document.body.appendChild(container);
        const root = createRoot(container);

        const cleanup = () => {
            setTimeout(() => {
                root.unmount();
                container.remove();
            }, 100);
        };

        const handleOk = () => {
            props.onOk?.();
            cleanup();
        };
        const handleCancel = () => {
            props.onCancel?.();
            cleanup();
        };

        root.render(
            <BaseModal
                title={props.title}
                content={props.content}
                onClose={handleCancel}
                actions={
                    <>
                        <button
                            className="px-4 py-1.5 rounded cursor-pointer bg-gray-200 text-gray-800"
                            onClick={handleCancel}
                        >
                            {props.cancelText ?? 'Huỷ'}
                        </button>
                        <button
                            autoFocus
                            className="px-4 py-1.5 rounded cursor-pointer bg-blue-500 text-white"
                            onClick={handleOk}
                        >
                            {props.okText ?? 'Đồng ý'}
                        </button>
                    </>
                }
                closeable={true}
            />
        );

        return { close: handleCancel };
    },

    modal(initialProps: ModalProps) {
        const container = document.createElement('div');
        document.body.appendChild(container);
        const root = createRoot(container);

        let currentProps = initialProps;

        const cleanup = () => {
            setTimeout(() => {
                root.unmount();
                container.remove();
            }, 100);
        };

        const handleClose = () => {
            currentProps.onClose?.();
            cleanup();
        };

        const render = () => {
            root.render(
                <BaseModal
                    title={currentProps.title}
                    content={currentProps.content}
                    onClose={handleClose}
                    actions={null}
                    fullWidth={currentProps.fullWidth}
                    fullHeight={currentProps.fullHeight}
                    closeable={currentProps.closeable}
                    footer={currentProps.footer}
                />
            );
        };

        render();

        return {
            close: handleClose,
            update: (newProps: Partial<ModalProps>) => {
                currentProps = { ...currentProps, ...newProps };
                render();
            }
        };
    }
};

export default AppAlert;
